import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { useOAuthStart, type OAuthProvider } from '@/api/queries/auth';
import { buildOAuthRedirectUri } from '@/utils/url';
import { paths } from '@/routes/paths';
import { IcMail } from '@/components/ui/icons';
import { useUIStore } from '@/store/ui';
import { isAPIError } from '@/api/error';
import styles from './styles.module.css';

// Auth providers: Email + Google + Facebook. Apple intentionally NOT
// offered (Addendum-01 Q1). Facebook can be hidden via
// VITE_FEATURE_FACEBOOK_OAUTH=false if the backend handler is not yet
// configured in a given environment.
const FACEBOOK_ENABLED =
  (import.meta.env.VITE_FEATURE_FACEBOOK_OAUTH ?? 'true').toLowerCase() !== 'false';

export function AuthLandingPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const oauthStart = useOAuthStart();
  const pushToast = useUIStore((s) => s.pushToast);

  const handleProvider = async (provider: OAuthProvider) => {
    try {
      const r = await oauthStart.mutateAsync({
        provider,
        redirectUri: buildOAuthRedirectUri(provider),
      });
      window.location.href = r.authorization_url;
    } catch (err) {
      // Backend may 404 on /auth/oauth/facebook/start if the provider is
      // not configured in this environment. Surface a friendly toast
      // instead of leaving the user staring at a frozen button.
      const key =
        isAPIError(err) && err.status === 404
          ? 'auth.oauth_unavailable'
          : 'errors.generic';
      pushToast({
        body: t(key, { defaultValue: t('errors.generic') }),
        variant: 'error',
      });
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.hero} aria-hidden />
      <div className={styles.cta}>
        <Button
          onClick={() => void handleProvider('google')}
          variant="inverted"
          size="lg"
          block
          loading={oauthStart.isPending}
        >
          <GoogleGlyph /> {t('auth.continue.google')}
        </Button>
        {FACEBOOK_ENABLED && (
          <Button
            onClick={() => void handleProvider('facebook')}
            variant="inverted"
            size="lg"
            block
            loading={oauthStart.isPending}
          >
            <FacebookGlyph /> {t('auth.continue.facebook')}
          </Button>
        )}
        <Button
          variant="inverted"
          size="lg"
          block
          startIcon={<IcMail />}
          onClick={() => navigate(paths.loginEmail)}
        >
          {t('auth.continue.email')}
        </Button>
        <p className={styles.agreement}>
          <Trans
            i18nKey="auth.agreement"
            components={{
              link: <a href="/policies" />,
            }}
          />
        </p>
      </div>
    </div>
  );
}

function GoogleGlyph(): JSX.Element {
  // Inline G logo (multi-colour). Sized small.
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" aria-hidden>
      <g>
        <path d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.79 2.73v2.27h2.9c1.7-1.56 2.69-3.86 2.69-6.64Z" fill="#4285F4" />
        <path d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.9-2.27c-.81.54-1.84.86-3.06.86-2.36 0-4.36-1.6-5.07-3.74H.96v2.34A9 9 0 0 0 9 18Z" fill="#34A853" />
        <path d="M3.93 10.67A5.4 5.4 0 0 1 3.63 9c0-.58.1-1.14.27-1.67V4.99H.96a9 9 0 0 0 0 8.02l2.97-2.34Z" fill="#FBBC05" />
        <path d="M9 3.58c1.33 0 2.51.46 3.45 1.36L15.05 2.4C13.46.92 11.43 0 9 0A9 9 0 0 0 .96 4.99l2.97 2.34C4.64 5.18 6.64 3.58 9 3.58Z" fill="#EA4335" />
      </g>
    </svg>
  );
}

function FacebookGlyph(): JSX.Element {
  // Inline Meta/Facebook "f" mark. Single-colour svg so it tints with
  // the surrounding button.
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#1877F2"
        d="M24 12.073C24 5.4 18.627 0 12 0S0 5.4 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.412c0-3.017 1.79-4.687 4.533-4.687 1.313 0 2.686.235 2.686.235v2.97h-1.513c-1.491 0-1.957.93-1.957 1.886v2.262h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073Z"
      />
    </svg>
  );
}
